import {
    DeleteTicketTaskDocument,
    DeleteTicketTaskMutation,
    DeleteTicketTaskMutationVariables,
    PortalEventType,
    PortalTaskUpdatedEvent,
    SaveTicketStateStatusDocument,
    SaveTicketStateStatusMutation,
    SaveTicketStateStatusMutationVariables,
    SaveTicketTaskDocument,
    SaveTicketTaskMutation,
    SaveTicketTaskMutationVariables,
    TicketTask, TicketTaskInput, TicketTaskState,
} from "../../__generated__/api";
import client from "../../api";
import { LoggingHandler } from "../logging/LoggingContext";
import TicketItem from "./ticket-item";
import UserCache from "./user-cache";

const IN_PROGRESS = new Set([
    TicketTaskState.Open,
    TicketTaskState.InProgress,
    TicketTaskState.OnHold,
]);

function isTask(typeTask: Omit<TicketTaskInput, "ticketId"> | TicketTask): typeTask is TicketTask {
    return (typeTask as TicketTask).id != null;
}

export default class TicketTasks {
    private _tasks: TicketTask[] = [];

    public constructor(private ticket: TicketItem, private onUpdate: (() => void), private userCache: UserCache) {
        // none
    }

    public async save(task: Omit<TicketTaskInput, "ticketId"> | TicketTask) {
        const isExisting = isTask(task);
        if (isExisting) {
            let copy = task;
            if (!copy.assignee && copy.assigneeId) {
                copy = { ...task };
                copy.assignee = await this.userCache.get(copy.assigneeId);
            }

            this.append([copy]);
            this.onUpdate();
        }

        LoggingHandler.instance.wrap(async () => {
            const created = await client.mutate<SaveTicketTaskMutation, SaveTicketTaskMutationVariables>({
                mutation: SaveTicketTaskDocument,
                variables: {
                    id: (isTask(task) ? task.id : undefined),
                    task: {
                        ticketId: this.ticket.ticket.id,
                        status: task.status,
                        assigneeId: task.assigneeId,
                        description: task.description,
                        dueDate: task.dueDate,
                        name: task.name,
                    },
                },
            });

            this.append([created.data?.saveTicketTask as TicketTask]);
            this.onUpdate();
        });
    }

    public saveStatus(id: string, status: TicketTaskState) {
        const task = this._tasks.find(current => current.id === id);
        if (!task) {
            return;
        }

        task.status = status;
        this.onUpdate();

        LoggingHandler.instance.wrap(async () => {
            await client.mutate<SaveTicketStateStatusMutation, SaveTicketStateStatusMutationVariables>({
                mutation: SaveTicketStateStatusDocument,
                variables: {
                    id: task.id,
                    state: status,
                },
            });
        });
    }

    public delete(id: string) {
        this._tasks = this._tasks.filter(current => current.id !== id);

        this.onUpdate();

        LoggingHandler.instance.wrap(async () => {
            await client.mutate<DeleteTicketTaskMutation, DeleteTicketTaskMutationVariables>({
                mutation: DeleteTicketTaskDocument,
                variables: {
                    id,
                },
            });
        });
    }

    public append(tasks: TicketTask[] | undefined | null) {
        if (tasks && tasks.isNotEmpty) {
            this._tasks = [...this._tasks, ...tasks.map(row => ({ ...row }))]
                .dedupe(row => row.id as string, (__, right) => right) as TicketTask[];
        }
    }

    public async processTicketUpdate(update: PortalTaskUpdatedEvent) {
        if (update.action === PortalEventType.TaskDeleted) {
            this._tasks = this._tasks.filter(current => current.id !== update.id);
        } else if (update.task) {
            let copy = update.task;
            if (!copy.assignee && copy.assigneeId) {
                copy = { ...update.task };
                copy.assignee = await this.userCache.get(copy.assigneeId);
            }
            this.append([copy]);
        }
        this.onUpdate();
    }

    public get tasks(): TicketTask[] {
        return this._tasks;
    }

    public get length() {
        return this._tasks.length;
    }

    public get finished(): TicketTask[] {
        return this.tasks.filter(row => !IN_PROGRESS.has(row.status ?? TicketTaskState.Open));
    }

    public get inProgress(): TicketTask[] {
        return this.tasks.filter(row => IN_PROGRESS.has(row.status ?? TicketTaskState.Open));
    }
}
